import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { GET_THREAD_REPLIES, ON_NEW_MESSAGE } from '../../graphql/operations';
import MessageBubble from './MessageBubble';
import MessageComposer from './MessageComposer';
import './ThreadPanel.css';

/**
 * ThreadPanel — slides in from the right to show a thread.
 *
 * Displays the parent message at top, flat list of replies below,
 * and a composer at the bottom that sends with threadParentId.
 *
 * Close affordances (post user-smoke): Esc anywhere or pointerdown
 * outside the panel both close — matches Slack's expectation. Clicks
 * inside the panel (composer, replies, hover toolbar) are explicitly
 * preserved by the ref guard. Clicks on the "Reply in thread" icon of
 * a sibling message bubble close the current panel and the parent's
 * `openThread` immediately re-opens it with the new parent — the panel
 * appears to swap parents seamlessly.
 */
function ThreadPanel({ parentMessage, conversationId, currentUserId, channelName, onClose }) {
  const panelRef = useRef(null);

  const { data, loading, refetch } = useQuery(GET_THREAD_REPLIES, {
    variables: { messageId: parentMessage.id, limit: 50 },
    fetchPolicy: 'cache-and-network'
  });

  const replies = useMemo(() => {
    // Drop soft-deleted replies entirely — same rule as the main channel
    // feed (MessageList.new.js). The DDB row sticks around for audit; we
    // just don't render an inline placeholder for it. Thread reply count
    // on the parent is server-managed and doesn't decrement on delete,
    // which is intentional — the divider label tracks total replies
    // including any that were later removed.
    return (data?.getThreadReplies?.messages || []).filter((r) => !r.deletedAt);
  }, [data]);

  // Defensive refetch on mount and whenever the parent message's
  // threadReplyCount changes upstream (set by applyThreadParentPatch when
  // ON_NEW_MESSAGE delivers a reply). The subscription path below also
  // calls refetch, but relying on a single ON_NEW_MESSAGE event was the
  // root of the "thread replies invisible to receiving user" bug — the
  // receiving user may have missed the subscription frame or have it
  // routed through a parallel WorkspaceContext-level handler. Mirroring
  // the count drives a fresh query whenever the parent updates.
  const parentReplyCount = parentMessage.threadReplyCount || 0;
  useEffect(() => {
    refetch();
  }, [parentMessage.id, parentReplyCount, refetch]);

  // Subscribe to new messages in this thread
  useSubscription(ON_NEW_MESSAGE, {
    variables: { conversationId },
    onData: ({ data: { data } }) => {
      const newMsg = data?.onNewMessage;
      if (newMsg && newMsg.threadParentId === parentMessage.id) {
        refetch();
      }
    }
  });

  // Scroll replies to bottom when new replies arrive
  useEffect(() => {
    const container = document.querySelector('.thread-panel-replies');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [replies.length]);

  // Esc to close (document-level keydown so the listener catches even
  // when focus is inside a contenteditable). Capture phase so any
  // descendant that uses Esc for its own modal (link popover, emoji
  // picker, mention menu) gets first shot — only if no other handler
  // calls preventDefault do we close the panel.
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key !== 'Escape') return;
      if (event.defaultPrevented) return;
      onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Click outside the panel closes it. Uses mousedown so the dismissal
  // fires before any click handlers on the new target — e.g. clicking
  // another message's "Reply in thread" icon will (a) dismiss the
  // current panel, then (b) parent's openThread fires on click, which
  // re-mounts the panel with the new parent. Net effect: clean swap.
  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(event.target)) return;
      // Skip clicks that landed on portal'd descendants (emoji picker,
      // link popover, mention menu, forward modal). They live outside
      // the panel ref but originate from the panel's composer.
      if (
        event.target.closest('.composer-v2-link-popover, em-emoji-picker, .composer-v2-emoji-popover, .beautiful-mentions-menu, [role="dialog"]')
      ) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [onClose]);

  return (
    <div className="thread-panel" ref={panelRef}>
      <div className="thread-panel-header">
        <h3 className="thread-panel-title">Thread</h3>
        <button
          type="button"
          className="thread-panel-close"
          onClick={onClose}
          aria-label="Close thread"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="thread-panel-parent">
        <MessageBubble
          message={parentMessage}
          currentUserId={currentUserId}
        />
      </div>

      <div className="thread-panel-divider">
        <span className="thread-panel-divider-line" />
        <span className="thread-panel-divider-label">
          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
        </span>
        <span className="thread-panel-divider-line" />
      </div>

      <div className="thread-panel-replies">
        {loading && replies.length === 0 && (
          <div className="thread-panel-loading">Loading replies...</div>
        )}
        {replies.map((reply) => (
          <MessageBubble
            key={reply.id}
            message={reply}
            currentUserId={currentUserId}
          />
        ))}
      </div>

      <div className="thread-panel-composer">
        <MessageComposer
          conversationId={conversationId}
          channelName={channelName}
          threadParentId={parentMessage.id}
        />
      </div>
    </div>
  );
}

export default ThreadPanel;
